/**
 * 后端服务 API 客户端
 * 处理与 Flask 后端的所有通信
 */

const API_BASE_URL = (() => {
  const envUrl = import.meta.env.VITE_API_URL?.trim();
  if (envUrl) {
    return envUrl.replace(/\/+$/, '');
  }

  // Production fallback: use same-origin /api to avoid accidental localhost calls.
  if (import.meta.env.PROD) {
    return '/api';
  }

  // Local development default.
  return 'http://localhost:5001/api';
})();

interface ProcessOptions {
  dpi?: number;
  jpeg_quality?: number;
  watermark_width?: number;
  watermark_height?: number;
}

interface ProcessResult {
  data: ArrayBuffer;
  filename: string;
  size: number;
  taskId: string;
}

/**
 * 上传文件到后端
 */
async function uploadFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Upload failed');
  }

  const result = await response.json();
  return result.task_id;
}

/**
 * 请求处理 PDF
 */
async function requestProcessing(taskId: string, options: ProcessOptions): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/process/${taskId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Processing failed');
  }
}

/**
 * 轮询任务状态
 */
async function pollTaskStatus(
  taskId: string, 
  onProgress: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/status/${taskId}`);
        
        if (!response.ok) {
          clearInterval(interval);
          reject(new Error('Failed to get task status'));
          return;
        }

        const status = await response.json();
        onProgress(status.progress);

        if (status.status === 'complete') {
          clearInterval(interval);
          resolve();
        } else if (status.status === 'error') {
          clearInterval(interval);
          reject(new Error(status.error || 'Processing error'));
        }
      } catch (error) {
        clearInterval(interval);
        reject(error);
      }
    }, 1000); // 每秒轮询一次

    // 超时保护（5分钟）
    setTimeout(() => {
      clearInterval(interval);
      reject(new Error('Processing timeout'));
    }, 5 * 60 * 1000);
  });
}

/**
 * 下载处理后的文件
 */
async function downloadResult(taskId: string): Promise<ArrayBuffer> {
  const response = await fetch(`${API_BASE_URL}/download/${taskId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Download failed');
  }

  return await response.arrayBuffer();
}

/**
 * 清理任务文件
 */
async function cleanupTask(taskId: string): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/cleanup/${taskId}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('Cleanup failed:', error);
  }
}

async function downloadImagesZip(taskId: string): Promise<ArrayBuffer> {
  const response = await fetch(`${API_BASE_URL}/download-images/${taskId}?format=jpeg&dpi=300`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Download images zip failed');
  }

  return await response.arrayBuffer();
}

/**
 * 完整的处理流程
 */
export async function processWithBackend(
  file: File,
  options: ProcessOptions,
  onProgress: (progress: number) => void
): Promise<ProcessResult> {
  let taskId: string | null = null;

  try {
    // 1. 上传文件
    onProgress(10);
    taskId = await uploadFile(file);

    // 2. 请求处理
    onProgress(20);
    await requestProcessing(taskId, options);

    // 3. 等待处理完成
    await pollTaskStatus(taskId, (progress) => {
      // 将后端进度映射到 20-90 范围
      const mappedProgress = 20 + (progress * 0.7);
      onProgress(mappedProgress);
    });

    // 4. 下载结果
    onProgress(95);
    const data = await downloadResult(taskId);

    onProgress(100);

    return {
      data,
      filename: `Cleaned-${file.name}`,
      size: data.byteLength,
      taskId,
    };
  } catch (error) {
    if (taskId) {
      setTimeout(() => cleanupTask(taskId!), 5000);
    }
    throw error;
  }
}

export async function downloadPageImagesZip(taskId: string): Promise<ArrayBuffer> {
  return downloadImagesZip(taskId);
}

export async function cleanupBackendTask(taskId: string): Promise<void> {
  return cleanupTask(taskId);
}

/**
 * 健康检查
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}
